import { useCallback, useEffect, useState } from 'react'

/** @typedef {'light' | 'dark'} Theme */

const STORAGE_KEY = 'roammyway-theme'

/** @returns {Theme} */
function currentTheme() {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

// Module-level store, not per-component state — every `useTheme()` caller
// (Header's toggle button, InteractiveGlobe's theme-matched video/poster)
// shares one value. A toggle in the header used to update only Header's own
// local state, leaving other components rendering a stale theme with no way
// to find out the class had changed underneath them.
let theme = typeof document === 'undefined' ? 'light' : currentTheme()
const listeners = new Set()

/** @param {Theme} next */
function applyTheme(next) {
  theme = next
  document.documentElement.classList.toggle('dark', next === 'dark')
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', next === 'dark' ? '#0d2230' : '#ffffff')
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* private mode — the in-session theme still applies */
  }
  listeners.forEach((listener) => listener(next))
}

export function useTheme() {
  const [value, setValue] = useState(theme)

  useEffect(() => {
    listeners.add(setValue)
    // Catch up in case the store changed between this component's first
    // render and this effect running.
    setValue(theme)
    return () => listeners.delete(setValue)
  }, [])

  const toggle = useCallback(() => applyTheme(theme === 'dark' ? 'light' : 'dark'), [])

  return { theme: value, toggle }
}
